<template>
    <b-modal
        :visible="isVisible"
        :title="title"
        :modal-class="{ 'inactivity-modal--locked': isLocked }"
        centered
        no-close-on-backdrop
        no-close-on-esc
        hide-header-close
        @hide="onHide"
    >
        <p v-if="isWarning" class="mb-0">
            You will be locked out due to inactivity in
            <strong>{{ remainingSeconds }}</strong> second<span v-if="remainingSeconds !== 1">s</span>.
        </p>
        <p v-else class="mb-0">
            Your session was locked due to inactivity. Signing you out&hellip;
        </p>

        <template #modal-footer>
            <b-button v-if="isWarning" variant="primary" @click="staySignedIn">Stay signed in</b-button>
            <b-button variant="outline-secondary" @click="signOutNow">Sign out now</b-button>
        </template>
    </b-modal>
</template>

<script lang="ts">
import { Component, Vue, Watch } from "vue-property-decorator";
import { idleInactivityState, isLockedPhase, type InactivityState } from "@/util/inactivity/inactivityStateMachine";

/**
 * Renders the inactivity warning and lock dialog. State is driven entirely by the SharedWorker-backed
 * timer via `$root.$data.inactivityState`; this component only displays it and surfaces the
 * "Stay signed in" / "Sign out now" controls. Built on <b-modal> so it traps focus and is marked
 * aria-modal — without that, keyboard/screen-reader users could Tab into the PHI behind the lock.
 * The app behind the lock is additionally blurred via the `app--inactivity-locked` class in App.vue.
 */
@Component({ name: "InactivityOverlay" })
export default class InactivityOverlay extends Vue {
    private now = Date.now();
    private ticker: ReturnType<typeof setInterval> | null = null;

    get state(): InactivityState {
        return this.$root.$data.inactivityState ?? idleInactivityState();
    }

    get isWarning(): boolean {
        return this.state.phase === "warning";
    }

    get isLocked(): boolean {
        return isLockedPhase(this.state.phase);
    }

    get isVisible(): boolean {
        return this.isWarning || this.isLocked;
    }

    get title(): string {
        return this.isWarning ? "Are you still there?" : "Session locked";
    }

    /** Countdown shown during the warning phase. Derived from the deadline and a `now` field that the
     *  ticker bumps once a second — no manually-synced mirror of the value. */
    get remainingSeconds(): number {
        const deadline = this.state.deadline;
        return deadline ? Math.max(0, Math.ceil((deadline - this.now) / 1000)) : 0;
    }

    @Watch("state", { immediate: true })
    onStateChange(): void {
        this.now = Date.now();
        // Only the warning phase shows a live countdown; the lock screen is static text, so don't
        // run a ticker there.
        if (this.isWarning && this.state.deadline) {
            this.startTicker();
        } else {
            this.stopTicker();
        }
    }

    staySignedIn(): void {
        this.$inactivityReset?.();
    }

    signOutNow(): void {
        this.$inactivitySignOut?.();
    }

    onHide(bvModalEvt: { preventDefault: () => void }): void {
        // Visibility is driven solely by inactivity state. Esc/backdrop closes are already disabled;
        // this guards against any stray programmatic hide while the overlay should still be showing.
        // When the state returns to "active", isVisible is already false here, so the close proceeds.
        if (this.isVisible) bvModalEvt.preventDefault();
    }

    private startTicker(): void {
        this.stopTicker();
        // Whole-second display, so tick once a second rather than 4x.
        this.ticker = setInterval(() => { this.now = Date.now(); }, 1000);
    }

    private stopTicker(): void {
        if (this.ticker) {
            clearInterval(this.ticker);
            this.ticker = null;
        }
    }

    beforeDestroy(): void {
        this.stopTicker();
    }
}
</script>

<!-- Not scoped: <b-modal> renders into <body>, outside this component's DOM, so a scoped selector
     would not reach it. The locked variant darkens the dialog; the app behind it is blurred via the
     app--inactivity-locked class on the root element in App.vue. -->
<style lang="scss">
.inactivity-modal--locked {
    .modal-content {
        background: rgba(20, 20, 30, 0.97);
        color: #fff;
        border: none;
        box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.5);
    }

    // The global outline-secondary fix (styles/custom.scss) is dark-on-light; on this dark
    // overlay flip it back to light-on-dark so "Sign out now" stays readable. `.btn.btn-...`
    // here is one notch more specific than the global rule so it deterministically wins.
    .btn.btn-outline-secondary {
        color: #fff;
        border-color: rgba(255, 255, 255, 0.65);

        &:hover,
        &:focus,
        &.active,
        &:not(:disabled):not(.disabled):active {
            color: #20202e;
            background-color: #fff;
            border-color: #fff;
        }
    }
}
</style>
